import React, { useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TextStyle, Color, FontSize } from "@tiptap/extension-text-style";
import Image from "@tiptap/extension-image";
import { TableKit } from "@tiptap/extension-table";
import TextAlign from "@tiptap/extension-text-align";
import Youtube from "@tiptap/extension-youtube";
import "./richtext.css";
import { api, formatApiError, resolveMedia } from "@/lib/api";
import { toast } from "sonner";
import {
  TextB, TextItalic, TextUnderline, ListBullets, ListNumbers, TextHOne, TextHTwo, TextHThree,
  LinkSimple, Image as ImageIcon, YoutubeLogo, Table as TableIcon, Palette, TextAlignLeft,
  TextAlignCenter, TextAlignRight, ArrowUUpLeft, ArrowUUpRight, TextT, Minus,
} from "@phosphor-icons/react";

const FONT_SIZES = [
  { label: "Small", value: "14px" },
  { label: "Normal", value: "16px" },
  { label: "Large", value: "20px" },
  { label: "X-Large", value: "26px" },
];

const Btn = ({ active, onClick, title, disabled, testid, children }) => (
  <button
    type="button"
    title={title}
    data-testid={testid}
    disabled={disabled}
    onClick={onClick}
    className={`w-8 h-8 flex items-center justify-center rounded-sm transition-colors disabled:opacity-30 ${
      active ? "bg-[#FF5722] text-black" : "text-white/70 hover:bg-white/10 hover:text-white"
    }`}
  >
    {children}
  </button>
);

const Sep = () => <span className="w-px h-6 bg-white/10 mx-0.5" />;

export const RichTextEditor = ({ value, onChange, testid = "rich-editor" }) => {
  const imgRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ link: { openOnClick: false, HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } } }),
      TextStyle,
      Color,
      FontSize,
      Image.configure({ inline: false }),
      TableKit.configure({ table: { resizable: true } }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Youtube.configure({ width: 640, height: 360, nocookie: true }),
    ],
    content: value || "",
    editorProps: { attributes: { class: "rt-content", "data-placeholder": "Write the rule content here…" } },
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  if (!editor) return null;

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/rules/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      editor.chain().focus().setImage({ src: resolveMedia(res.data.url), alt: res.data.name }).run();
      toast.success("Image inserted");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally { if (imgRef.current) imgRef.current.value = ""; }
  };

  const addLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL (leave blank to remove):", prev);
    if (url === null) return;
    if (url === "") { editor.chain().focus().extendMarkRange("link").unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const addVideo = () => {
    const url = window.prompt("YouTube video URL:");
    if (!url) return;
    editor.commands.setYoutubeVideo({ src: url });
  };

  const addImageUrl = () => {
    const url = window.prompt("Image URL:");
    if (url) editor.chain().focus().setImage({ src: url }).run();
  };

  return (
    <div className="rt-editor border border-white/15 bg-[#1A1A1A]" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-0.5 border-b border-white/10 p-1.5 sticky top-0 bg-[#161616] z-10">
        <Btn testid="rt-undo" title="Undo" onClick={() => editor.chain().focus().undo().run()}><ArrowUUpLeft size={16} /></Btn>
        <Btn testid="rt-redo" title="Redo" onClick={() => editor.chain().focus().redo().run()}><ArrowUUpRight size={16} /></Btn>
        <Sep />
        <Btn testid="rt-bold" title="Bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><TextB size={16} weight="bold" /></Btn>
        <Btn testid="rt-italic" title="Italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><TextItalic size={16} /></Btn>
        <Btn testid="rt-underline" title="Underline" active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()}><TextUnderline size={16} /></Btn>
        <Sep />
        <Btn testid="rt-h1" title="Heading 1" active={editor.isActive("heading", { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}><TextHOne size={16} /></Btn>
        <Btn testid="rt-h2" title="Heading 2" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><TextHTwo size={16} /></Btn>
        <Btn testid="rt-h3" title="Heading 3" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><TextHThree size={16} /></Btn>
        <Sep />
        <span className="flex items-center gap-1 text-white/50 px-1"><TextT size={15} /></span>
        <select
          data-testid="rt-fontsize"
          title="Font size"
          onChange={(e) => { const v = e.target.value; if (v) editor.chain().focus().setFontSize(v).run(); else editor.chain().focus().unsetFontSize().run(); }}
          value={editor.getAttributes("textStyle").fontSize || "16px"}
          className="bg-[#1A1A1A] border border-white/15 text-white/70 text-xs px-1.5 py-1 rounded-sm focus:outline-none"
        >
          {FONT_SIZES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
        <label title="Text color" className="w-8 h-8 flex items-center justify-center rounded-sm text-white/70 hover:bg-white/10 cursor-pointer relative">
          <Palette size={16} />
          <input
            type="color"
            data-testid="rt-color"
            onInput={(e) => editor.chain().focus().setColor(e.target.value).run()}
            value={editor.getAttributes("textStyle").color || "#ffffff"}
            className="absolute inset-0 opacity-0 cursor-pointer"
          />
        </label>
        <Sep />
        <Btn testid="rt-bullet" title="Bullet list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><ListBullets size={16} /></Btn>
        <Btn testid="rt-ordered" title="Numbered list" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListNumbers size={16} /></Btn>
        <Sep />
        <Btn testid="rt-align-left" title="Align left" active={editor.isActive({ textAlign: "left" })} onClick={() => editor.chain().focus().setTextAlign("left").run()}><TextAlignLeft size={16} /></Btn>
        <Btn testid="rt-align-center" title="Align center" active={editor.isActive({ textAlign: "center" })} onClick={() => editor.chain().focus().setTextAlign("center").run()}><TextAlignCenter size={16} /></Btn>
        <Btn testid="rt-align-right" title="Align right" active={editor.isActive({ textAlign: "right" })} onClick={() => editor.chain().focus().setTextAlign("right").run()}><TextAlignRight size={16} /></Btn>
        <Sep />
        <Btn testid="rt-link" title="Insert link" active={editor.isActive("link")} onClick={addLink}><LinkSimple size={16} /></Btn>
        <Btn testid="rt-image-upload" title="Upload image" onClick={() => imgRef.current?.click()}><ImageIcon size={16} /></Btn>
        <Btn testid="rt-image-url" title="Image from URL" onClick={addImageUrl}><TextT size={16} /></Btn>
        <Btn testid="rt-video" title="Embed YouTube video" onClick={addVideo}><YoutubeLogo size={16} /></Btn>
        <Btn testid="rt-table" title="Insert 3×3 table" onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}><TableIcon size={16} /></Btn>
        <Btn testid="rt-hr" title="Divider" onClick={() => editor.chain().focus().setHorizontalRule().run()}><Minus size={16} /></Btn>
        <input ref={imgRef} type="file" accept="image/*" className="hidden" onChange={uploadImage} data-testid="rt-image-input" />
      </div>

      {editor.isActive("table") && (
        <div className="flex flex-wrap gap-1 border-b border-white/10 p-1.5 text-[11px]">
          {[
            ["Add col", () => editor.chain().focus().addColumnAfter().run()],
            ["Del col", () => editor.chain().focus().deleteColumn().run()],
            ["Add row", () => editor.chain().focus().addRowAfter().run()],
            ["Del row", () => editor.chain().focus().deleteRow().run()],
            ["Del table", () => editor.chain().focus().deleteTable().run()],
          ].map(([label, fn]) => (
            <button key={label} type="button" onClick={fn} className="px-2 py-1 border border-white/15 text-white/60 hover:text-white hover:border-[#FF5722] uppercase tracking-wide">{label}</button>
          ))}
        </div>
      )}

      <EditorContent editor={editor} />
    </div>
  );
};
